
import React from "react";
import { Classname } from "@rivtn/noseur";
import Btn from "../common/Btn";
import { MERCH } from "../../data";
import { i18nManager } from "../../i18n";
import { Utils } from "../../../../../utils";
import styles from "../../assets/style.module.css";
import { isPageVisible, pagePath, stripMarkdown } from "../../helpers";
import { ITeamNews, ITeamWebsite, ITournamentEdition } from "../../../../../services";

interface IFeature {
    badge: string;
    title: string;
    text?: string;
    image?: string;
    cta: string;
    to?: { pathname: string; search?: string; };
    href?: string;
}

export interface FeatureCardsProps {
    basePath: string;
    news?: ITeamNews[];
    website: ITeamWebsite;
    tournaments?: ITournamentEdition[];
}

function FeatureCard({ feature: f, large }: { feature: IFeature; large?: boolean; }) {
    return (<div className={Classname.build(styles["content-card"], { [styles["content-card-lg"]]: large })}>
        {f.image && (<img className={styles["content-card-img"]} src={f.image} alt="" />)}
        <div className={styles["content-card-shade"]} />
        <span className={styles["card-badge"]}>{f.badge}</span>
        <div className={styles["content-card-body"]}>
            <h2 className={large ? styles["d2"] : styles["d3"]}>{f.title}</h2>
            {(large && f.text) && (<p className={Classname.build(styles["lead"], styles["clamp-2"])}>{f.text}</p>)}
            <Btn size={large ? "md" : "sm"} variant={large ? "primary" : "text"} to={f.to} href={f.href} target={f.href ? "_blank" : undefined}>{f.cta}</Btn>
        </div>
    </div>);
}

function FeatureCards({ website, basePath, news, tournaments }: FeatureCardsProps) {

    const labels = i18nManager.Labels.home;
    const [latest] = news ?? [];
    const [activeTournament] = tournaments ?? [];
    const [social, username] = Object.entries(website.social_accounts ?? {})[0] ?? [];

    const main: IFeature | undefined = isPageVisible(website, "MERCH")
        ? { badge: labels.new, title: MERCH[0].name, image: MERCH[0].img, cta: labels.shop_now, to: { pathname: pagePath(basePath, "MERCH") } }
        : (latest ? { badge: latest.extra_data?.category || labels.latest_news, title: latest.title, text: stripMarkdown(latest.content, 120), image: latest.extra_data?.featured_image, cta: i18nManager.Labels.read_more, to: { pathname: pagePath(basePath, "NEWS"), search: `article=${latest.external_id}` } } : undefined);

    const side: IFeature[] = [
        ...(activeTournament ? [{ badge: labels.competing, title: activeTournament.name, image: activeTournament.banner_url, cta: labels.view_activities, to: isPageVisible(website, "ACTIVITIES") ? { pathname: pagePath(basePath, "ACTIVITIES") } : undefined, href: isPageVisible(website, "ACTIVITIES") ? undefined : `/to/${activeTournament.tournament_id}` }] : []),
        ...(social ? [{ badge: labels.fans, title: labels.join_community, cta: labels.follow, href: Utils.getSocialLink(social, username) }] : []),
        ...((isPageVisible(website, "MERCH") && latest) ? [{ badge: latest.extra_data?.category || labels.latest_news, title: latest.title, image: latest.extra_data?.featured_image, cta: i18nManager.Labels.read_more, to: { pathname: pagePath(basePath, "NEWS"), search: `article=${latest.external_id}` } }] : []),
    ].slice(0, 2);

    if (!main && !side.length) return null;

    return (<section className={Classname.build(styles["section-tight"], styles["bg-white-4"])}>
        <div className={styles["container"]}>
            <div className={Classname.build(styles["feature-grid"], { [styles["feature-single"]]: !main || !side.length })}>
                {main && (<FeatureCard large feature={main} />)}
                {side.length > 0 && (<div className={styles["feature-side"]}>
                    {side.map((f) => (<FeatureCard key={f.title} feature={f} />))}
                </div>)}
            </div>
        </div>
    </section>);
}

export default FeatureCards;
